import { del, get, post, put } from "../../../core/api/client";
import { API_ENDPOINTS } from "../../../core/api/endpoints";
import { API_BASE_URL } from "../../../core/config/environment";

const sanitizeLimit = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return 20;
  return Math.min(100, Math.max(1, Math.trunc(numeric)));
};

const sanitizeOffset = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return 0;
  return Math.max(0, Math.trunc(numeric));
};

export const toDateLabel = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const toRelativeTimeLabel = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "just now";
  }

  const deltaMs = Date.now() - parsed.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;

  if (deltaMs < hour) {
    const minutes = Math.max(1, Math.floor(deltaMs / minute));
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }

  if (deltaMs < day) {
    const hours = Math.floor(deltaMs / hour);
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  if (deltaMs < week) {
    const days = Math.floor(deltaMs / day);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }

  const weeks = Math.floor(deltaMs / week);
  return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
};

const normalizeCaseStatus = (status) => {
  const value = String(status || "open").toLowerCase().replace(/\s+/g, "_");
  return value === "closed" ? "closed" : "open";
};

const toDataUrl = (raw, contentType = "application/octet-stream") => {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (/^data:/i.test(value)) return value;
  return `data:${contentType};base64,${value}`;
};

const resolveAttachmentUrl = (message) => {
  const directUrl =
    message?.attachment_url ||
    message?.attachmentUrl ||
    message?.attachment_download_url ||
    message?.attachment_signed_url ||
    message?.attachment_presigned_url;
  if (directUrl) {
    return String(directUrl).trim();
  }

  const contentType =
    message?.attachment_contenttype ||
    message?.attachment_mimetype ||
    "application/octet-stream";

  const rawContent =
    message?.attachment_content ||
    message?.attachment_base64 ||
    message?.attachment_data;

  return toDataUrl(rawContent, contentType);
};

export const mapTicketToSupportCase = (ticket) => {
  const ticketId = Number(ticket?.id);
  const createdAt = ticket?.created_at || ticket?.updated_at;
  const updatedAt = ticket?.updated_at || ticket?.created_at;

  return {
    id: Number.isFinite(ticketId) ? ticketId : ticket?.id,
    title: ticket?.case_title || "Support Case",
    subtitle: `Ticket #${ticket?.id ?? "-"}`,
    date: toDateLabel(createdAt),
    createdAgo: toRelativeTimeLabel(createdAt),
    updatedAgo: toRelativeTimeLabel(updatedAt),
    status: normalizeCaseStatus(ticket?.case_status),
    message: ticket?.case_description || "",
    replies: [],
    supportResponses: [],
  };
};

export const mapTicketMessageToSupportResponse = (message) => {
  const hasAttachment = Boolean(
    message?.attachment_filename ||
      message?.attachment_content ||
      message?.attachment_base64 ||
      message?.attachment_data,
  );
  const attachmentName = message?.attachment_filename || "Attachment";
  const attachmentUrl = resolveAttachmentUrl(message);

  return {
    id: message?.id,
    from: "DTS Contact Support",
    date: toDateLabel(message?.message_timestamp || message?.created_at),
    message: message?.message_content || "",
    hasAttachment,
    attachmentName,
    attachmentUrl,
  };
};

export const extractSupportTicket = (response) => {
  if (!response || response.error) return null;
  if (response?.supportTicket && typeof response.supportTicket === "object") {
    return response.supportTicket;
  }
  if (response?.ticket && typeof response.ticket === "object") {
    return response.ticket;
  }
  if (response?.id || response?.case_title || response?.case_description) {
    return response;
  }
  return null;
};

export const extractTicketMessage = (response) => {
  if (!response || response.error) return null;
  if (response?.ticketMessage && typeof response.ticketMessage === "object") {
    return response.ticketMessage;
  }
  if (response?.message && typeof response.message === "object") {
    return response.message;
  }
  if (response?.id || response?.message_content) {
    return response;
  }
  return null;
};

export const extractSupportTicketList = (response) => {
  if (!response || response.error) return [];
  if (Array.isArray(response?.supportTickets)) return response.supportTickets;
  if (Array.isArray(response?.tickets)) return response.tickets;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const extractTicketMessages = (response) => {
  if (!response || response.error) return [];
  if (Array.isArray(response?.ticketMessages)) return response.ticketMessages;
  if (Array.isArray(response?.messages)) return response.messages;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const createSupportTicket = async (payload) => {
  return post(API_ENDPOINTS.SUPPORT_TICKETS.CREATE, payload);
};

export const getSupportTicketsByClient = async (
  clientId,
  params = {},
) => {
  const { limit = 20, offset = 0, status } = params;
  const safeLimit = sanitizeLimit(limit);
  const safeOffset = sanitizeOffset(offset);

  return get(
    API_ENDPOINTS.SUPPORT_TICKETS.LIST_BY_CLIENT(
      clientId,
      safeLimit,
      safeOffset,
      status,
    ),
  );
};

export const getSupportTicketById = async (id) => {
  return get(API_ENDPOINTS.SUPPORT_TICKETS.GET_BY_ID(id));
};

export const updateSupportTicketById = async (id, payload) => {
  return put(API_ENDPOINTS.SUPPORT_TICKETS.UPDATE_BY_ID(id), payload);
};

export const deleteSupportTicketById = async (id) => {
  return del(API_ENDPOINTS.SUPPORT_TICKETS.DELETE_BY_ID(id));
};

// Ticket Messages

export const createTicketMessage = async (payload) => {
  return post(API_ENDPOINTS.TICKET_MESSAGES.CREATE, payload);
};

export const getTicketMessagesByTicket = async (ticketId, params = {}) => {
  const { limit = 20, offset = 0 } = params;
  const safeLimit = sanitizeLimit(limit);
  const safeOffset = sanitizeOffset(offset);

  return get(
    API_ENDPOINTS.TICKET_MESSAGES.LIST_BY_TICKET(ticketId, safeLimit, safeOffset),
  );
};

export const getTicketMessageById = async (id) => {
  return get(API_ENDPOINTS.TICKET_MESSAGES.GET_BY_ID(id));
};

export const updateTicketMessageById = async (id, payload) => {
  return put(API_ENDPOINTS.TICKET_MESSAGES.UPDATE_BY_ID(id), payload);
};

export const deleteTicketMessageById = async (id) => {
  return del(API_ENDPOINTS.TICKET_MESSAGES.DELETE_BY_ID(id));
};

const extractAttachmentUrl = (response) => {
  if (!response || response.error) {
    return "";
  }

  const directUrl =
    response?.attachment_url ||
    response?.attachmentUrl ||
    response?.attachment_download_url ||
    response?.attachment_signed_url ||
    response?.attachment_presigned_url ||
    response?.attachment_upload_url;
  if (directUrl) {
    return String(directUrl).trim();
  }

  const nestedUrl =
    response?.attachment?.url ||
    response?.data?.attachment_url ||
    response?.data?.attachmentUrl ||
    response?.data?.attachment_upload_url;

  return nestedUrl ? String(nestedUrl).trim() : "";
};

export const uploadTicketMessageAttachment = async (uploadUrl, file) => {
  const targetUrl = String(uploadUrl || "").trim();
  if (!targetUrl || !file) {
    return {
      error: {
        message: "Attachment upload URL is missing.",
      },
    };
  }

  try {
    const response = await fetch(targetUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });

    if (!response.ok) {
      return {
        error: {
          message: "Attachment upload failed. Please try again.",
          statusCode: response.status,
        },
      };
    }

    return { success: true };
  } catch {
    return {
      error: {
        message:
          "Attachment upload was blocked by CORS policy. Please ask backend to allow your app origin for S3 PUT uploads.",
      },
    };
  }
};

export const getTicketMessageAttachmentUrl = async (id) => {
  const response = await get(
    API_ENDPOINTS.TICKET_MESSAGES.GET_ATTACHMENT_BY_MESSAGE_ID(id),
  );

  if (response?.error) {
    return response;
  }

  const attachmentUrl = extractAttachmentUrl(response);
  if (!attachmentUrl) {
    return {
      error: {
        message: "Attachment URL is unavailable.",
      },
    };
  }

  if (/^(https?:|data:|blob:)/i.test(attachmentUrl)) {
    return { attachment_url: attachmentUrl };
  }

  const base = String(API_BASE_URL || "").replace(/\/+$/, "");
  const normalizedUrl = attachmentUrl.startsWith("/")
    ? `${base}${attachmentUrl}`
    : attachmentUrl;

  return { attachment_url: normalizedUrl };
};
